<script lang="ts">
  /**
   * A ranked list of players: a `Group` of table `Row`s (rank, name with an
   * optional second line, score). The player's own row is the `me` tone
   * (accent, heavier) and says `youLabel` on its second line; when it ranks
   * below `limit` it follows the top rows, its rank telling how far down.
   * Interlock's daily top 20; TwinTen's tournament board.
   *
   * The game fetches; this part only draws what it is told. `state`:
   *
   * - `ready`: the rows, or `emptyText` when there are none;
   * - `loading`: `loadingText` over the rows the game already has (if any);
   * - `offline`: `offlineText` over the last rows the game kept (if any);
   * - `guest`: `guestText` and `guestAction` (the game's Sign in), no rows.
   *
   * Every word is a prop. Test ids: `testid` on the block (default
   * `ui-leaderboard`, with `data-state`); `testids.row` on each row (default
   * `ui-leaderboard-row`), `testids.own` on the player's own (default
   * `ui-leaderboard-own`), `testids.note` on the state's sentence (default
   * `ui-leaderboard-note`); a row's own `testid` wins over the list's.
   */
  import type { Snippet } from 'svelte';

  import type { ContentState, LeaderboardRow } from '../templates/menuTypes.js';
  import Group from './Group.svelte';
  import Row from './Row.svelte';

  interface Props {
    readonly rows: readonly LeaderboardRow[];
    /** The player's own row, highlighted. */
    readonly ownId?: string | null | undefined;
    /** How many top rows to show (default 20); the own row follows them when it ranks lower. */
    readonly limit?: number;
    readonly state?: ContentState;
    /** The small heading inside the block ("Today's top 20"); also its accessible name. */
    readonly title?: string | undefined;
    readonly emptyText: string;
    readonly loadingText?: string | undefined;
    readonly offlineText?: string | undefined;
    readonly guestText?: string | undefined;
    /** Under `guestText`: the game's way to sign in. */
    readonly guestAction?: Snippet | undefined;
    /** The own row's second line ("You"), before its `detail`. */
    readonly youLabel?: string | undefined;
    readonly testid?: string | undefined;
    readonly testids?:
      { readonly row?: string; readonly own?: string; readonly note?: string } | undefined;
  }
  const {
    rows,
    ownId,
    limit = 20,
    state = 'ready',
    title,
    emptyText,
    loadingText,
    offlineText,
    guestText,
    guestAction,
    youLabel,
    testid = 'ui-leaderboard',
    testids,
  }: Props = $props();

  function rowTestid(row: LeaderboardRow): string {
    if (row.testid !== undefined) return row.testid;
    return isOwn(row)
      ? (testids?.own ?? 'ui-leaderboard-own')
      : (testids?.row ?? 'ui-leaderboard-row');
  }

  const isOwn = (row: LeaderboardRow): boolean =>
    ownId !== undefined && ownId !== null && row.id === ownId;

  /** The top rows, then the own row when it ranks below them. */
  const shown = $derived.by((): readonly LeaderboardRow[] => {
    if (state === 'guest') return [];
    const top = rows.slice(0, Math.max(0, limit));
    if (top.some(isOwn)) return top;
    const own = rows.find(isOwn);
    return own === undefined ? top : [...top, own];
  });

  const note = $derived.by((): string | null => {
    if (state === 'loading') return loadingText ?? null;
    if (state === 'offline') return offlineText ?? null;
    if (state === 'guest') return guestText ?? null;
    return rows.length === 0 ? emptyText : null;
  });

  function below(row: LeaderboardRow): string | undefined {
    if (!isOwn(row) || youLabel === undefined) return row.detail;
    return row.detail === undefined ? youLabel : `${youLabel} · ${row.detail}`;
  }
</script>

<div class="ui-board" data-testid={testid} data-state={state} aria-busy={state === 'loading'}>
  <Group as="section" label={title} name={title}>
    {#if note !== null}
      <p
        class="ui-board-note"
        class:ui-board-note--ruled={shown.length > 0}
        role="status"
        data-testid={testids?.note ?? 'ui-leaderboard-note'}
      >
        {note}
      </p>
    {/if}
    {#if state === 'guest' && guestAction !== undefined}
      <div class="ui-board-action">{@render guestAction()}</div>
    {/if}
    {#each shown as row (row.id)}
      <Row
        rank={row.rank}
        label={row.name}
        below={below(row)}
        value={row.score}
        tone={isOwn(row) ? 'me' : 'default'}
        testid={rowTestid(row)}
      />
    {/each}
  </Group>
</div>

<style>
  .ui-board {
    display: flex;
    flex-direction: column;
  }

  .ui-board-note {
    margin: 0;
    padding: 0.85rem 1rem;
    font: var(--ui-text-label);
    font-weight: 400;
    line-height: 1.4;
    color: var(--ui-ink-2);
  }

  /* Over rows: the same hairline the rows have between them. */
  .ui-board-note--ruled {
    border-bottom: 1px solid var(--ui-line);
  }

  .ui-board-action {
    display: flex;
    padding: 0 1rem 0.85rem;
  }
</style>
